import {
  DOCUMENTS_CACHE_RETRY_COUNT,
  DOCUMENTS_CACHE_STALE_TIME,
} from '@constants/documents.constants';
import { AnySendingSearchParams, SendingsDashboardType } from '@domain/types';
import type {
  FetchSendingRequestsV2CountResponse,
  FetchSendingRequestsV2Response,
  IntegrationV2,
  IntegrationV2WithEncounter,
  PatientV2,
  PatientWithDetailsV2,
  ProfessionalRecipientDetailV2,
  ProfessionalRecipientV2,
  SendingRequestDetailV2,
  SendingRequestListRowV2,
  SendingRequestV2Common,
  SendingRequestV2Patch,
} from '@lifen-sending/services/sendingRequestsV2/sendingRequestsV2.types';
import { QueryClient } from '@tanstack/react-query';
// The neutral module, not @utils/caching.util: that one imports invalidateSendingRequestsV2Queries
// from this file, so importing it back here would create a cycle.
import { hasActiveFilters } from '@utils/dashboardFilters.util';
import { senderV2ToIdentityReference } from '@utils/identities.util';

export const SENDING_REQUESTS_V2_QUERY_KEY = 'SENDING_REQUESTS_V2_DOCUMENTS';

// Every v2 key is `[PREFIX, scope, ...]`. The scope sits at the same index in all three shapes, so
// list / count / detail entries can never be mistaken for one another whatever the rest of the key
// holds — and the prefix comes first, so all three stay swept by the invalidate/reset helpers below.
// This module is the only place that knows the layout: read it through the builders and
// `isSendingRequestsV2ListKey`, never by indexing a key yourself.
type SendingRequestsV2KeyScope = 'list' | 'count' | 'detail';
const SCOPE_INDEX = 1;
// list / count carry dashboardType then identity right after the scope segment; detail carries
// its id there instead — see the three key builders below.
const DASHBOARD_TYPE_INDEX = SCOPE_INDEX + 1;
const IDENTITY_INDEX = SCOPE_INDEX + 2;
const DETAIL_ID_INDEX = SCOPE_INDEX + 1;
// The count params sit right after the identity segment — see getSendingRequestsV2CountQueryKey.
const COUNT_PARAMS_INDEX = SCOPE_INDEX + 3;

// All three v2 queries share one caching policy, deliberately: a shorter staleTime on any of them
// would let it refresh on its own at remount while the others stay frozen, showing a total — or a
// detail — inconsistent with the displayed rows. They must only move through an explicit
// invalidation.
export const SENDING_REQUESTS_V2_QUERY_OPTIONS = {
  staleTime: DOCUMENTS_CACHE_STALE_TIME,
  retry: DOCUMENTS_CACHE_RETRY_COUNT,
} as const;

// Split out of useSendingRequestsV2.hook.ts: that hook imports prepareSearchParamsForCaching
// from common/utils/caching.util.ts, which in turn needs to invalidate this cache — keeping the
// primitives here avoids a circular import between the two.
export function getSendingRequestsV2QueryKey({
  dashboardType,
  identity,
  params,
}: {
  dashboardType: SendingsDashboardType;
  identity?: string;
  params?: AnySendingSearchParams;
}): unknown[] {
  return [
    SENDING_REQUESTS_V2_QUERY_KEY,
    'list' satisfies SendingRequestsV2KeyScope,
    dashboardType,
    identity ?? 'all',
    ...(params ? [params] : []),
  ];
}

// The count only depends on the filters: paging and sorting must not appear in the key, otherwise
// every page change or sort would refetch it. Built by omission rather than by allow-list so that
// any filter added to AnySendingSearchParams is taken into account without touching this function.
export function toSendingRequestsV2CountParams(
  params: AnySendingSearchParams,
): AnySendingSearchParams {
  const {
    page,
    perPage,
    sortBy,
    sortDirection,
    sortByAuthoredOn,
    sortByPatientLastname,
    sortByReceivedOn,
    ...countParams
  } = params;

  return countParams;
}

export function getSendingRequestsV2CountQueryKey({
  dashboardType,
  identity,
  params,
}: {
  dashboardType: SendingsDashboardType;
  identity?: string;
  params: AnySendingSearchParams;
}): unknown[] {
  return [
    SENDING_REQUESTS_V2_QUERY_KEY,
    'count' satisfies SendingRequestsV2KeyScope,
    dashboardType,
    identity ?? 'all',
    toSendingRequestsV2CountParams(params),
  ];
}

// The detail is neither dashboard- nor identity-scoped, hence the shorter key. It shares the prefix
// so it stays swept by invalidateSendingRequestsV2Queries / resetSendingRequestsV2Queries.
export function getSendingRequestV2DetailQueryKey(id: string): unknown[] {
  return [SENDING_REQUESTS_V2_QUERY_KEY, 'detail' satisfies SendingRequestsV2KeyScope, id];
}

export function isSendingRequestsV2ListKey(queryKey: readonly unknown[]): boolean {
  return queryKey[SCOPE_INDEX] === ('list' satisfies SendingRequestsV2KeyScope);
}

export type SendingRequestsV2Scope =
  | { all: true }
  | {
      /** One or more dashboards: a write on a shared entity targets several at once. */
      dashboardType: SendingsDashboardType | SendingsDashboardType[];
      /**
       * Identity reference `${type}/${id}`. The workspace ('all') bucket always matches.
       * `'any'` = the write touches an entity shared between senders (patient, trusted
       * contact), so every identity bucket matches too.
       */
      identities: string[] | 'any';
      /** Without it, no `detail` entry matches. */
      documentIds?: string[];
      /** Which key scopes to sweep. Defaults to all three. */
      keyScopes?: SendingRequestsV2KeyScope[];
    };

function matchesSendingRequestsV2Scope(
  queryKey: readonly unknown[],
  scope: Exclude<SendingRequestsV2Scope, { all: true }>,
): boolean {
  const keyScope = queryKey[SCOPE_INDEX] as SendingRequestsV2KeyScope;
  if (!(scope.keyScopes ?? ['list', 'count', 'detail']).includes(keyScope)) return false;

  if (keyScope === 'detail') {
    return !!scope.documentIds?.includes(queryKey[DETAIL_ID_INDEX] as string);
  }

  const dashboardTypes = Array.isArray(scope.dashboardType)
    ? scope.dashboardType
    : [scope.dashboardType];
  if (!dashboardTypes.includes(queryKey[DASHBOARD_TYPE_INDEX] as SendingsDashboardType)) {
    return false;
  }

  const identity = queryKey[IDENTITY_INDEX];
  return (
    identity === 'all' ||
    scope.identities === 'any' ||
    scope.identities.includes(identity as string)
  );
}

export function invalidateSendingRequestsV2Queries(
  queryClient: QueryClient,
  scope: SendingRequestsV2Scope,
) {
  if ('all' in scope) {
    return queryClient.invalidateQueries(
      { queryKey: [SENDING_REQUESTS_V2_QUERY_KEY] },
      { cancelRefetch: false },
    );
  }

  return queryClient.invalidateQueries(
    {
      queryKey: [SENDING_REQUESTS_V2_QUERY_KEY],
      predicate: (query) => matchesSendingRequestsV2Scope(query.queryKey, scope),
    },
    { cancelRefetch: false },
  );
}

// Unlike invalidateSendingRequestsV2Queries, this clears the cached data before refetching, so the
// table visibly reloads — used by the manual "Rafraîchir" button for feedback that it did something.
export function resetSendingRequestsV2Queries(queryClient: QueryClient) {
  return queryClient.resetQueries({ queryKey: [SENDING_REQUESTS_V2_QUERY_KEY] });
}

/**
 * Patches every matched count entry by `delta` instead of invalidating it outright, so the sidebar
 * badge and the table's own count don't flash through a refetch on every mutation.
 *
 * A filtered bucket (see `hasActiveFilters`) can't be patched this way: the server recomputes which
 * documents match the filter, and the client has no way to know whether this write moved a document
 * in or out of it — so it's invalidated instead, with `refetchType: 'none'` since the 24h staleTime
 * (see SENDING_REQUESTS_V2_QUERY_OPTIONS) would otherwise never let it refetch on its own.
 *
 * The patch is terminal: an unfiltered bucket is never invalidated on top of it. It used to be, and
 * since `refetchOnMount` / `refetchOnWindowFocus` are left at their defaults, that left the count
 * permanently stale — every remount and every focus refetched a total the client had already
 * computed, which is the one thing this function exists to avoid.
 */
export function applySendingRequestsV2CountDelta(
  queryClient: QueryClient,
  {
    dashboardType,
    identities,
    delta,
  }: {
    dashboardType: SendingsDashboardType | SendingsDashboardType[];
    identities: string[] | 'any';
    delta: number;
  },
): void {
  const countEntries = queryClient.getQueriesData<FetchSendingRequestsV2CountResponse>({
    queryKey: [SENDING_REQUESTS_V2_QUERY_KEY],
    predicate: (query) =>
      matchesSendingRequestsV2Scope(query.queryKey, {
        dashboardType,
        identities,
        keyScopes: ['count'],
      }),
  });

  for (const [queryKey, data] of countEntries) {
    const isFiltered = hasActiveFilters(queryKey[COUNT_PARAMS_INDEX] as AnySendingSearchParams);

    if (isFiltered) {
      queryClient.invalidateQueries(
        { queryKey, exact: true, refetchType: 'none' },
        { cancelRefetch: false },
      );
      continue;
    }

    // `computing: true` carries no total to add a delta to, same as no cached data at all — and
    // neither wants invalidating either: the 2s poll (see useSendingRequestsV2Count) settles the
    // first, and a bucket with no data fetches on its next mount regardless.
    if (delta === 0 || !data || data.computing) continue;

    queryClient.setQueryData<FetchSendingRequestsV2CountResponse>(queryKey, {
      ...data,
      total: Math.max(0, data.total + delta),
    });
  }
}

// Scans every list page cached under the v2 prefix — the list key embeds the search params, so
// there are N cached pages, not one — and returns the first row matching `id`. The predicate
// restricts the scan to 'list' entries: 'count' and 'detail' entries don't have the
// `{ data: SendingRequestV2[] }` shape this reads. Optimistic rows are skipped: they're local
// placeholders (see optimisticSendingRequestV2.util.ts), not server data fit to seed a detail view.
/**
 * A cached list row is a sending request minus the patient's contact block, which only `:show`
 * renders — see `seedDetailFromListRowV2`. Returned with the age of the list response that carried
 * it, so the detail query is primed without claiming the data is fresh.
 *
 * First match wins, not the freshest. Only sound because the detail revalidates on every mount.
 */
export function findSendingRequestV2InCache(
  queryClient: QueryClient,
  id: string,
): { row: SendingRequestListRowV2; dataUpdatedAt: number | undefined } | undefined {
  const listEntries = queryClient.getQueriesData<FetchSendingRequestsV2Response>({
    queryKey: [SENDING_REQUESTS_V2_QUERY_KEY],
    predicate: (query) => isSendingRequestsV2ListKey(query.queryKey),
  });

  for (const [queryKey, data] of listEntries) {
    const row = data?.data.find((candidate) => candidate.id === id && !candidate.isOptimistic);
    if (row) return { row, dataUpdatedAt: queryClient.getQueryState(queryKey)?.dataUpdatedAt };
  }

  return undefined;
}

/**
 * The identity to scope a v2 invalidation to, when a mutation's own response doesn't carry the
 * sender: falls back to the row already cached from the sending request's last list fetch.
 * Returns undefined when genuinely unknown — never a guess.
 */
export function findSenderIdentityV2InCache(
  queryClient: QueryClient,
  documentId: string,
): string | undefined {
  const sender = findSendingRequestV2InCache(queryClient, documentId)?.row.sender;
  return sender && senderV2ToIdentityReference(sender);
}

const withEmptyPatientDetails = (patient: PatientV2 | null): PatientWithDetailsV2 | null =>
  patient && {
    ...patient,
    ipp: null,
    nir: null,
    ins: null,
    insQualification: null,
    address: [],
    telecoms: [],
  };

// A list row has no encounter (`view :show` only) — null reads as "not loaded", same as the
// patient blocks below, not as "no encounter".
const withEmptyEncounter = (integrations: IntegrationV2[]): IntegrationV2WithEncounter[] =>
  integrations.map((integration) => ({ ...integration, encounter: null }));

// `null` is also what the blueprint serves when a field does not apply, so readers need no special
// case — see `ProfessionalRecipientDetailV2`.
const withEmptyProfessionalRecipientDetails = (
  recipients: ProfessionalRecipientV2[],
): ProfessionalRecipientDetailV2[] =>
  recipients.map((recipient) => ({
    ...recipient,
    qualifications: null,
    isManuallyCreated: null,
    directoryAddresses: null,
  }));

// Widens a list row into the detail shape to seed the detail query. The empty blocks mean "not
// loaded", not "the patient has none" — refetchOnMount bounds them to the first frame.
export function seedDetailFromListRowV2(row: SendingRequestListRowV2): SendingRequestDetailV2 {
  return {
    ...row,
    requesterDisplayName: null,
    patient: withEmptyPatientDetails(row.patient),
    ehrPatient: withEmptyPatientDetails(row.ehrPatient),
    integrations: withEmptyEncounter(row.integrations),
    professionalRecipients: withEmptyProfessionalRecipientDetails(row.professionalRecipients),
  };
}

export type SendingRequestV2Snapshot = {
  id: string;
  detail: SendingRequestDetailV2 | undefined;
  lists: [readonly unknown[], FetchSendingRequestsV2Response | undefined][];
};

/**
 * Applies one patch to every cached copy: the detail singleton and the row in each list page.
 * Optimistic rows are skipped. Returns the snapshot to roll back to.
 *
 * The updater returns only the keys it changes, and `SendingRequestV2Common` excludes the patient,
 * so one patch is safe on both the rich detail and a poor list row.
 */
export function updateSendingRequestV2Cache(
  queryClient: QueryClient,
  id: string,
  updater: (document: SendingRequestV2Common) => SendingRequestV2Patch,
): SendingRequestV2Snapshot {
  const detailKey = getSendingRequestV2DetailQueryKey(id);
  const detail = queryClient.getQueryData<SendingRequestDetailV2>(detailKey);

  if (detail) {
    const patch = updater(detail);
    // A patch is typed against the list row's plain `IntegrationV2[]` — the lowest common shape a
    // patch may safely express — so a patch that replaces `integrations` (e.g. a connector toggle)
    // erases `encounter` at the type level even though the patcher's own spread keeps it at
    // runtime. Re-attach it here by `resourceId` so the merge stays sound for the detail cache too.
    const integrations: IntegrationV2WithEncounter[] = patch.integrations
      ? patch.integrations.map((integration) => ({
          ...integration,
          encounter:
            detail.integrations.find((i) => i.resourceId === integration.resourceId)?.encounter ??
            null,
        }))
      : detail.integrations;

    // Same reasoning as `integrations` above: a patch is typed against the list row's plain
    // `ProfessionalRecipientV2[]`, so it cannot carry `qualifications` / `isManuallyCreated` /
    // `directoryAddresses`. Re-attach them from the existing detail entry by `resourceId`; a
    // recipient the detail hasn't seen yet (e.g. one just added) falls back to `null`, same as
    // `seedDetailFromListRowV2`.
    const professionalRecipients: ProfessionalRecipientDetailV2[] = patch.professionalRecipients
      ? patch.professionalRecipients.map((recipient) => {
          const existing = detail.professionalRecipients.find(
            (r) => r.resourceId === recipient.resourceId,
          );
          return {
            ...recipient,
            qualifications: existing?.qualifications ?? null,
            isManuallyCreated: existing?.isManuallyCreated ?? null,
            directoryAddresses: existing?.directoryAddresses ?? null,
          };
        })
      : detail.professionalRecipients;

    queryClient.setQueryData<SendingRequestDetailV2>(detailKey, {
      ...detail,
      ...patch,
      integrations,
      professionalRecipients,
    });
  }

  const lists: SendingRequestV2Snapshot['lists'] = [];

  const listEntries = queryClient.getQueriesData<FetchSendingRequestsV2Response>({
    queryKey: [SENDING_REQUESTS_V2_QUERY_KEY],
    predicate: (query) => isSendingRequestsV2ListKey(query.queryKey),
  });

  for (const [queryKey, data] of listEntries) {
    const holdsRow = data?.data.some((row) => row.id === id && !row.isOptimistic);
    if (!data || !holdsRow) continue;

    lists.push([queryKey, data]);
    queryClient.setQueryData<FetchSendingRequestsV2Response>(queryKey, {
      ...data,
      data: data.data.map((row) =>
        row.id === id && !row.isOptimistic ? { ...row, ...updater(row) } : row,
      ),
    });
  }

  return { id, detail, lists };
}

/** Undoes `updateSendingRequestV2Cache`. */
export function restoreSendingRequestV2Cache(
  queryClient: QueryClient,
  snapshot: SendingRequestV2Snapshot,
): void {
  for (const [queryKey, data] of snapshot.lists) {
    queryClient.setQueryData(queryKey, data);
  }

  if (snapshot.detail) {
    queryClient.setQueryData(getSendingRequestV2DetailQueryKey(snapshot.id), snapshot.detail);
  }
}
